<template>
  <div>
    <b-card>
      <breadcrumbs :breadcrumb-items="breadcrumbItems" />
      <DspPartnerToolbar
        v-if="Object.keys(dsppartner).length !== 0"
        :dsppartner="dsppartner"
        closing
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4>{{ $t('dsppartners.navigation.devices') }}</h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="isLoading"
              @refresh="getDeviceOsStatsData"
              @input="toggleRange"
            />
          </div>
          <div class="mt-25">
            <b-form-radio-group
              v-model="filterGroup"
              button-variant="outline-primary"
              :options="filterGroupOptions"
              buttons
              size="sm"
              name="radios-btn-default"
            />
          </div>
        </div>

      </div>

      <div
        v-if="isLoading"
        class="text-center my-3"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-table
        v-else-if="rows.length"
        :items="rows"
        :fields="fields"
        striped
        responsive
        small
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        foot-clone
        class="text-center"
      >

        <template #head(os)>
          <div>{{ $t('stats.os') }}</div>
        </template>
        <template #head(device)>
          <div>{{ $t('stats.device') }}</div>
        </template>
        <template #head(impressions)>
          <div>{{ $t('stats.impressions') }}</div>
          <div class="mt-25">
            {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('impressions')) }}
          </div>
        </template>
        <template #head(spent)>
          <div>{{ $t('stats.spent') }}</div>
          <div class="mt-25">
            {{ currency('USD', sumRevenueMinusTax()) }}
          </div>
        </template>
        <template #head(tax)>
          <div>{{ $t('stats.tax') }}</div>
          <div class="mt-25">
            {{ currency('USD', sum('tax')) }}
          </div>
        </template>
        <template #cell(impressions)="{ item }">
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(item.impressions) }}
        </template>
        <template #cell(spent)="{ item }">
          {{ item.revenue && item.tax ? currency('USD', item.revenue - item.tax) : currency('USD', item.spent || 0) }}
        </template>
        <template #cell(tax)="{ item }">
          {{ currency('USD', item.tax) }}
        </template>
        <template #cell(os)="{ item }">
          <b-button
            v-if="item.os"
            variant="link"
            class="p-0 text-decoration-none"
            @click="goToDeviceOsDetails(item.os, 'os')"
          >
            {{ item.os }}
          </b-button>
          <span v-else>{{ $t('stats.no_value') }}</span>
        </template>
        <template #cell(device)="{ item }">
          <b-button
            v-if="item.device"
            variant="link"
            class="p-0 text-decoration-none"
            @click="goToDeviceOsDetails(item.device, 'device')"
          >
            {{ item.device }}
          </b-button>
          <span v-else>{{ $t('stats.no_value') }}</span>
        </template>
        <template #foot(impressions)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('impressions')) }}
        </template>
        <template #foot(device)>
          {{ $t('Records') }}: {{ totalRecords }}
        </template>
        <template #foot(os)>
          {{ $t('Records') }}: {{ totalRecords }}
        </template>
        <template #foot(spent)>
          {{ currency('USD', sumRevenueMinusTax()) }}
        </template>
        <template #foot(tax)>
          {{ currency('USD', sum('tax')) }}
        </template>

      </b-table>

      <!-- Pagination -->
      <div
        v-if="rows.length"
        class="d-flex justify-content-between align-items-center mt-2"
      >
        <div class="d-flex align-items-center">
          <span class="mr-2">{{ $t('message.pagelength') }}:</span>
          <b-form-select
            v-model="limit"
            :options="limitOptions"
            size="sm"
            class="w-auto"
            @change="onLimitChange"
          />
          <span class="ml-2"> {{ $t('message.of') }} {{ totalRecords }} {{ $t('message.pageText2') }}</span>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRecords"
          :per-page="limit"
          :limit="5"
          align="right"
          size="md"
          @change="onPageChange"
        />
      </div>

      <div
        v-else
        class="text-center text-muted my-3"
      >
        {{ $t('No data for this period') }}
      </div>

    </b-card>
  </div>
</template>
<script>
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import {
  BCard,
  BTable,
  VBTooltip,
  BSpinner,
  BButton,
  BFormRadioGroup,
  BFormSelect,
  BPagination,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import DspPartnerToolbar from '../DspPartnerToolbar.vue'
import StatsNavigation from './StatsNavigation.vue'
import StatsDateRangeFilter from './StatsDateRangeFilter.vue'

export default {
  name: 'DeviceOsStats',
  components: {
    BCard,
    BTable,
    Breadcrumbs,
    DspPartnerToolbar,
    StatsNavigation,
    BSpinner,
    BButton,
    BFormRadioGroup,
    BFormSelect,
    BPagination,
    StatsDateRangeFilter,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      currency: currencyFormatter,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      getFormattedDate,
      rows: [],
      filterGroup: 'os',
      filterGroupOptions: [
        { value: 'device', text: 'Device' },
        { value: 'os', text: 'Os' },
      ],
      sortBy: 'impressions',
      sortDesc: true,
      sortDirection: 'desc',
      dsppartner: {},
      breadcrumbItems: [],
      isLoading: false,
      // Pagination
      limit: 20,
      currentPage: 1,
      totalRecords: 0,
      limitOptions: [
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
    }
  },
  computed: {
    selectedRangeFormated() {
      if (!this.rangePicker || !Array.isArray(this.rangePicker) || this.rangePicker.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        const today = getFormattedDate(new Date())
        return { from: today, to: today }
      }

      return {
        from: this.rangePicker[0],
        to: this.rangePicker[1],
      }
    },
    fields() {
      if (this.filterGroup === 'device') {
        return [
          { key: 'device', label: 'device', sortable: true },
          { key: 'impressions', label: 'impressions', sortable: true },
          { key: 'spent', label: 'spent', sortable: true },
          { key: 'tax', label: 'tax', sortable: true },
        ]
      }
      return [
        { key: 'os', label: 'os', sortable: true },
        { key: 'impressions', label: 'impressions', sortable: true },
        { key: 'spent', label: 'spent', sortable: true },
        { key: 'tax', label: 'tax', sortable: true },
      ]
    },

  },
  watch: {
    filterGroup() {
      this.currentPage = 1 // Reset to first page when changing filter
      this.getDeviceOsStatsData()
    },
    '$route.path': {
      handler() {
        // Обновляем filterGroup при изменении роута
        if (this.$route.path.includes('/stats/device')) {
          this.filterGroup = 'device'
        } else if (this.$route.path.includes('/stats/os')) {
          this.filterGroup = 'os'
        }
      },
      immediate: false,
    },
  },
  created() {
    this.getPartnerData()

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
      this.getDeviceOsStatsData() // Вызываем только один раз при инициализации
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
      this.getDeviceOsStatsData() // Вызываем только один раз при инициализации
    }
  },
  methods: {
    async getPartnerData() {
      const responseData = await useJwt.getDsppartner(this.$route.params.id)
      this.dsppartner = responseData.data.dsppartner || {}
      this.setBreabcrumbs()
    },
    async getDeviceOsStatsData() {
      this.isLoading = true
      const { from, to } = this.selectedRangeFormated
      try {
        let response
        if (this.filterGroup === 'device') {
          response = await useJwt.getDsppartnerStatsDeviceTotal(
            this.$route.params.id,
            {
              from,
              to,
              type: 'daily',
              limit: this.limit,
              offset: (this.currentPage - 1) * this.limit,
            },
          )
        } else {
          response = await useJwt.getDsppartnerStatsOsTotal(
            this.$route.params.id,
            {
              from,
              to,
              type: 'daily',
              limit: this.limit,
              offset: (this.currentPage - 1) * this.limit,
            },
          )
        }
        this.rows = (response.data.items || []).map(item => ({
          ...item,
          spent: item.revenue,
        }))
        this.totalRecords = response.data.total || this.rows.length
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      } finally {
        this.isLoading = false
      }
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('DSP partners'),
          to: '/dsppartners/',
        },
        {
          text: this.dsppartner.name,
          to: `/dsppartners/${this.dsppartner._id}/`,
        },
        {
          text: this.$t('Statistics'),
          to: `/dsppartners/${this.dsppartner._id}/stats`,
        },
        {
          text: this.$t('dsppartners.navigation.devices'),
          active: true,
        },
      ]
    },
    sum(key) {
      return this.rows.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    sumRevenueMinusTax() {
      return this.rows.reduce((acc, r) => acc + ((r.revenue || 0) - (r.tax || 0)), 0)
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Update state and URL in one go
        this.rangePicker = [from, to]
        // Обновляем URL без перезагрузки страницы
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        this.currentPage = 1 // Reset to first page when changing date range
        this.getDeviceOsStatsData()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    goToDeviceOsDetails(value, type) {
      const routeName = type === 'device'
        ? 'dashboard-dsppartner-stats-device-details'
        : 'dashboard-dsppartner-stats-os-details'

      const paramName = type === 'device' ? 'deviceId' : 'osId'

      this.$router.push({
        name: routeName,
        params: {
          id: this.$route.params.id,
          [paramName]: value,
        },
        query: this.$route.query,
      })
    },
    onPageChange(page) {
      this.currentPage = page
      this.getDeviceOsStatsData()
    },
    onLimitChange() {
      this.currentPage = 1 // Reset to first page when changing limit
      this.getDeviceOsStatsData()
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('dspStatsDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}
.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}
</style>
